import { useCallback, useState } from "react";

import type { Invoice } from "~/shared/schemas/invoice-schema.ts";

export type InvoiceStatus =
	| "openInvoices"
	| "pendingPayment"
	| "paidInvoices"
	| "allInvoices";

export const useInvoiceSelection = (data: Invoice[]) => {
	const [selectedInvoices, setSelectedInvoices] = useState<Invoice[]>([]);
	const [selectedInvoiceByAction, setSelectedInvoiceByAction] =
		useState<Invoice | null>(null);

	const handleSelect = useCallback(
		(id: string | number, selectedByAction = false) => {
			const invoice = data.find((item) => item.invoiceID === id);
			if (invoice && !selectedByAction) {
				setSelectedInvoices((prev) => {
					if (!prev.some((item) => item.invoiceID === id)) {
						return [...prev, invoice];
					}
					return prev;
				});
			} else if (invoice && selectedByAction) {
				setSelectedInvoiceByAction(invoice);
			}
		},
		[data],
	);

	const handleDeselect = useCallback((id: string | number) => {
		setSelectedInvoices((prev) => prev.filter((item) => item.invoiceID !== id));
	}, []);

	const handleSelectAll = useCallback(
		(selected: boolean) => {
			setSelectedInvoices(selected ? data : []);
		},
		[data],
	);

	const removeInvoice = useCallback((invoiceId: string) => {
		setSelectedInvoices((prev) =>
			prev.filter((item) => item.invoiceID !== invoiceId),
		);
	}, []);

	const clearSelection = useCallback(() => {
		setSelectedInvoices([]);
		setSelectedInvoiceByAction(null);
	}, []);

	return {
		selectedInvoices,
		selectedInvoiceByAction,
		handleSelect,
		handleDeselect,
		handleSelectAll,
		removeInvoice,
		clearSelection,
		isAllSelected: data.length > 0 && selectedInvoices.length === data.length,
	};
};
